/**
 * "Talk to my agent": boot. The agent integration includes this on every
 * page; it is all that loads until someone asks for the agent.
 *
 * The layout opens the panel with
 *   window.dispatchEvent(new CustomEvent('agent:open', { detail: { trigger: button } }))
 * (`trigger` is the pill that was pressed: the panel lays itself over it and
 * returns focus to it). Optional: `detail.prompt` asks a first question.
 * `agent:close` closes it. Mark the button `data-agent-open` and hovering or
 * focusing it starts the download early.
 */
type Panel = typeof import('./panel');

let loading: Promise<Panel> | null = null;
const load = () => (loading ??= import('./panel'));

function triggerOf(e: Event): HTMLElement | null {
  const detail = (e as CustomEvent<{ trigger?: unknown } | undefined>).detail;
  if (detail?.trigger instanceof HTMLElement) return detail.trigger;
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body && !active.closest('.agent-ui')) return active;
  return document.querySelector<HTMLElement>('[data-agent-open]');
}

addEventListener('agent:open', (e) => {
  const trigger = triggerOf(e);
  const prompt = (e as CustomEvent<{ prompt?: unknown } | undefined>).detail?.prompt;
  load().then((m) => m.openAgent({ trigger, prompt: typeof prompt === 'string' ? prompt.slice(0, 500) : undefined }));
});
addEventListener('agent:close', () => {
  loading?.then((m) => m.closeAgent());
});

const intent = (e: Event) => {
  if ((e.target as Element | null)?.closest?.('[data-agent-open]')) load();
};
document.addEventListener('pointerover', intent, { passive: true });
document.addEventListener('focusin', intent);

// A link can open the agent: colin.gillingh.am/?agent=open
if (new URLSearchParams(location.search).get('agent') === 'open') {
  const go = () => load().then((m) => m.openAgent({ trigger: document.querySelector<HTMLElement>('[data-agent-open]') }));
  if (document.readyState === 'complete') go();
  else addEventListener('load', go, { once: true });
}
